/**
 * Language Switcher Component
 *
 * Dropdown or minimal button to switch between languages.
 * Integrates with LanguageContext for global state management.
 */

import React, { useState } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Language } from '../../i18n/config';

interface LanguageSwitcherProps {
  /** Visual variant */
  variant?: 'dropdown' | 'minimal' | 'buttons';
  /** Show language name label */
  showLabel?: boolean;
  /** Show flag emoji */
  showFlag?: boolean;
  /** Custom className */
  className?: string;
  /** For dark backgrounds (sidebar) */
  darkMode?: boolean;
}

export function LanguageSwitcher({
  variant = 'dropdown',
  showLabel = true,
  showFlag = true,
  className = '',
  darkMode = false,
}: LanguageSwitcherProps) {
  const {
    language,
    setLanguage,
    toggleLanguage,
    supportedLanguages,
    getLanguageName,
    getLanguageFlag,
  } = useLanguage();

  const [isOpen, setIsOpen] = useState(false);

  // Minimal variant - single toggle button
  if (variant === 'minimal') {
    return (
      <button
        onClick={toggleLanguage}
        className={`p-2 rounded-lg transition-colors ${
          darkMode
            ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
            : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'
        } ${className}`}
        aria-label={`Change language to ${getLanguageName(language === 'es' ? 'en' : 'es')}`}
        title={`${getLanguageFlag(language)} ${getLanguageName(language)}`}
      >
        <Globe className="w-5 h-5" />
      </button>
    );
  }

  // Buttons variant - all languages visible
  if (variant === 'buttons') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {supportedLanguages.map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              language === lang
                ? darkMode
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700'
                : darkMode
                  ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
            aria-pressed={language === lang}
          >
            {showFlag && <span className="mr-1">{getLanguageFlag(lang)}</span>}
            {showLabel ? getLanguageName(lang) : lang.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }

  // Dropdown variant (default)
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          darkMode
            ? 'hover:bg-slate-700 text-slate-300'
            : 'hover:bg-slate-100 text-slate-700'
        }`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="w-4 h-4" />
        {showFlag && <span className="text-sm">{getLanguageFlag(language)}</span>}
        {showLabel && <span className="text-sm font-medium">{getLanguageName(language)}</span>}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Dropdown menu */}
          <div
            className={`absolute z-50 mt-2 py-1 w-40 rounded-lg shadow-lg border ${
              darkMode
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-slate-200'
            }`}
            style={{ bottom: '100%', left: 0, marginBottom: '0.5rem' }}
            role="listbox"
            aria-label="Select language"
          >
            {supportedLanguages.map((lang) => (
              <button
                key={lang}
                onClick={() => {
                  setLanguage(lang);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                  language === lang
                    ? darkMode
                      ? 'bg-slate-700 text-blue-400'
                      : 'bg-blue-50 text-blue-600'
                    : darkMode
                      ? 'text-slate-300 hover:bg-slate-700'
                      : 'text-slate-700 hover:bg-slate-50'
                }`}
                role="option"
                aria-selected={language === lang}
              >
                {showFlag && <span>{getLanguageFlag(lang)}</span>}
                <span className="flex-1">{getLanguageName(lang)}</span>
                {language === lang && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default LanguageSwitcher;
